import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import StatCard from '../../components/common/StatCard';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import { getCompanyProfile, getCompanyJobs, getCompanyApplications } from '../../api/company';
import { useToast } from '../../context/ToastContext';

const CompanyDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [profileRes, jobsRes, appsRes] = await Promise.all([
          getCompanyProfile(),
          getCompanyJobs(),
          getCompanyApplications()
        ]);
        
        setProfile(profileRes.data);
        setJobs(jobsRes.data || []);
        setApplications(appsRes.data || []);
      } catch (err) {
        console.error('Failed to load dashboard', err);
        addToast('Failed to load dashboard data', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [addToast]);

  if (loading) {
    return (
      <div className="page-wrapper">
        <Navbar />
        <div className="main-content flex justify-center items-center h-full"><Loader fullPage /></div>
        <Footer />
      </div>
    );
  }

  const activeJobs = jobs.filter(j => j.status !== 'closed');
  const shortlisted = applications.filter(a => ['Shortlisted', 'Interview Scheduled'].includes(a.status));
  const selected = applications.filter(a => a.status === 'Selected');

  return (
    <div className="page-wrapper dashboard-page">
      <Navbar />
      
      <div className="dashboard-header !bg-gray-900">
        <div className="container">
          <h1 className="dashboard-greeting">Hello, {profile?.name || 'Company'}</h1>
          <p className="dashboard-sub">Employer Portal Dashboard</p>
        </div>
      </div>
      
      <main className="container mt-8 flex-1">
        {profile?.status === 'pending' && (
          <div className="alert alert-warning mb-6">
            <span className="text-xl">⚠️</span>
            <div>
              <strong>Account under review</strong>
              <p className="mt-1">Your company profile is currently being reviewed by our administrators. You can post jobs, but they won't be visible to students until your account is approved.</p>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mb-6">
          <h2 className="heading-2">Overview</h2>
          <Button variant="primary" onClick={() => navigate('/company/jobs/new')}>
            + Post New Job
          </Button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-4 gap-6 mb-8">
          <StatCard title="Active Jobs" value={activeJobs.length} icon="💼" />
          <StatCard title="Total Applicants" value={applications.length} icon="👥" />
          <StatCard title="Shortlisted" value={shortlisted.length} icon="⭐" />
          <StatCard title="Selected" value={selected.length} icon="🎉" />
        </div>
        
        <div className="grid grid-2 gap-8">
          {/* Recent Jobs */}
          <div className="card h-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="heading-3">Recent Jobs</h3>
              <Link to="/company/jobs" className="text-sm font-semi text-primary hover:underline">View All</Link>
            </div>
            
            {jobs.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📝</div>
                <p className="empty-desc">You haven't posted any jobs yet.</p>
                <Link to="/company/jobs/new"><Button className="mt-4">Post a Job</Button></Link>
              </div>
            ) : (
              <div className="flex-col gap-3">
                {jobs.slice(0, 5).map(job => (
                  <div key={job.id} className="flex justify-between items-center p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors">
                    <div>
                      <h4 className="font-semi text-gray-900">{job.title}</h4>
                      <div className="text-xs text-gray-500 mt-1">{job.employment_type} • {job.location}</div>
                    </div>
                    <Badge variant={job.status === 'closed' ? 'gray' : 'success'}>
                      {job.status === 'closed' ? 'Closed' : 'Active'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Applications */}
          <div className="card h-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="heading-3">Recent Applicants</h3>
              <Link to="/company/applicants" className="text-sm font-semi text-primary hover:underline">Manage All</Link>
            </div>
            
            {applications.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">👀</div>
                <p className="empty-desc">No applications received yet.</p>
              </div>
            ) : (
              <div className="flex-col gap-3">
                {applications.slice(0, 5).map(app => (
                  <div key={app.id} className="flex justify-between items-center p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex gap-3 items-center">
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
                        {app.student?.full_name?.charAt(0) || 'S'}
                      </div>
                      <div>
                        <h4 className="font-semi text-gray-900 text-sm">{app.student?.full_name || 'Student Name'}</h4>
                        <div className="text-xs text-gray-500">{app.opportunity?.title || 'Unknown Job'}</div>
                      </div>
                    </div>
                    <Badge variant={
                      app.status === 'Selected' ? 'success' :
                      app.status === 'Rejected' ? 'danger' :
                      app.status === 'Interview Scheduled' ? 'primary' :
                      app.status === 'Shortlisted' ? 'accent' : 'gray'
                    }>
                      {app.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CompanyDashboard;